import { useState, useEffect } from 'react';
import { getCurrentUser, actualizarUsuario, desactivarUsuario } from '../services/usuario';
import '../styles/Configuracion.css';

export default function Configuracion() {
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    password: ''
  });
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);

  useEffect(() => {
    const cargarUsuario = async () => {
      try {
        const data = await getCurrentUser();
        setUsuario(data);
        setFormData({
          nombre: data.nombre,
          password: ''
        });
      } catch (error) {
        console.error('Error cargando usuario:', error);
        setMensaje({ tipo: 'error', texto: 'Error al cargar datos del usuario' });
      } finally {
        setLoading(false);
      }
    };
    cargarUsuario();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensaje({ tipo: '', texto: '' });
    
    const dataToUpdate = {};
    if (formData.nombre !== usuario.nombre) dataToUpdate.nombre = formData.nombre;
    if (formData.password) dataToUpdate.password = formData.password;
    
    if (Object.keys(dataToUpdate).length === 0) {
      setMensaje({ tipo: 'info', texto: 'No hay cambios para guardar' });
      setEditando(false);
      return;
    }
    
    try {
      const usuarioActualizado = await actualizarUsuario(dataToUpdate);
      setUsuario(usuarioActualizado);
      setFormData({
        nombre: usuarioActualizado.nombre,
        password: ''
      });
      setMensaje({ tipo: 'success', texto: 'Perfil actualizado correctamente' });
      setEditando(false);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.message || 'Error al actualizar perfil' });
    }
  };

  const handleDesactivarCuenta = async () => {
    try {
      await desactivarUsuario();
      // Limpiar sesión y redirigir al login
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    } catch (error) {
      setMensaje({ tipo: 'error', texto: error.message || 'Error al desactivar cuenta' });
      setMostrarConfirmacion(false);
    }
  };

  if (loading) {
    return (
      <div className="config-loading">
        <div className="spinner"></div>
        <p>Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="config-container">
      <div className="config-header">
        <h1>⚙️ Configuración</h1>
        <p>Gestiona tu perfil y preferencias de cuenta</p>
      </div>

      <div className="config-grid">
        <div className="config-card perfil-card">
          <div className="card-header">
            <span className="card-icon">👤</span>
            <h2>Perfil de usuario</h2>
          </div>
          
          {!editando ? (
            <div className="perfil-vista">
              <div className="campo">
                <label>Nombre</label>
                <p>{usuario?.nombre}</p>
              </div>
              <div className="campo">
                <label>Email</label>
                <p>{usuario?.email} <span className="email-nota">(no editable)</span></p>
              </div>
              <div className="campo">
                <label>Rol</label>
                <p>{usuario?.rol || 'usuario'}</p>
              </div>
              <div className="campo">
                <label>Fecha de registro</label>
                <p>{new Date(usuario?.fecha_registro).toLocaleDateString()}</p>
              </div>
              <button className="btn-editar" onClick={() => setEditando(true)}>
                ✏️ Editar perfil
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="perfil-form">
              <div className="campo-input">
                <label>Nombre</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="campo-input">
                <label>Email</label>
                <input
                  type="email"
                  value={usuario?.email}
                  disabled
                  className="email-disabled"
                />
                <small>El email no se puede cambiar por seguridad</small>
              </div>
              <div className="campo-input">
                <label>Nueva contraseña (opcional)</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Dejar en blanco para no cambiar"
                />
              </div>
              <div className="form-acciones">
                <button type="submit" className="btn-guardar">💾 Guardar cambios</button>
                <button type="button" className="btn-cancelar" onClick={() => {
                  setEditando(false);
                  setFormData({
                    nombre: usuario.nombre,
                    password: ''
                  });
                }}>Cancelar</button>
              </div>
            </form>
          )}
        </div>

        <div className="config-card seguridad-card">
          <div className="card-header">
            <span className="card-icon">🔒</span>
            <h2>Seguridad</h2>
          </div>
          
          <div className="info-seguridad">
            <div className="campo">
              <label>Autenticación</label>
              <p>{usuario?.auth_provider === 'google' ? 'Google' : 'Email y contraseña'}</p>
            </div>
            <div className="campo">
              <label>Estado de cuenta</label>
              <p className={usuario?.activo ? 'activo' : 'inactivo'}>
                {usuario?.activo ? '✅ Activa' : '❌ Inactiva'}
              </p>
            </div>
          </div>

          <div className="accion-peligrosa">
            <h4>⚠️ Zona peligrosa</h4>
            <p>Desactivar tu cuenta impedirá el acceso. Podrás reactivarla contactando a soporte.</p>
            <button className="btn-eliminar-cuenta" onClick={() => setMostrarConfirmacion(true)}>
              🚫 Desactivar cuenta
            </button>
          </div>
        </div>
      </div>

      {mensaje.texto && (
        <div className={`config-mensaje ${mensaje.tipo}`}>
          {mensaje.texto}
        </div>
      )}

      {mostrarConfirmacion && (
        <div className="modal-overlay" onClick={() => setMostrarConfirmacion(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>¿Desactivar cuenta?</h2>
              <button className="modal-close" onClick={() => setMostrarConfirmacion(false)}>×</button>
            </div>
            <p>Tu cuenta quedará desactivada y no podrás acceder. Para reactivarla, contacta con soporte.</p>
            <p style={{ color: '#f97316', fontSize: '14px', marginTop: '8px' }}>
              ⚠️ Tus campañas y contenido se conservarán, pero no estarán accesibles.
            </p>
            <div className="modal-acciones">
              <button className="btn-cancelar" onClick={() => setMostrarConfirmacion(false)}>Cancelar</button>
              <button className="btn-confirmar-eliminar" onClick={handleDesactivarCuenta}>Sí, desactivar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}